'use client';

import { Conditional } from 'components/pcc-components';
import { usePartDiagram } from 'hooks/use-part-diagram/parts-diagram-context';
import { useEffect, useState } from 'react';
import PartsTableWrapper from 'components/parts-table-wrapper';
import { DealerResponse } from 'services/dealer';
import { isEmpty } from '@cat-ecom/common/utils/commonUtils';
import PartsOverview from 'components/parts-overview';
import { PartDetailsAndSchematicResponse } from 'types/product-diagram';
import {
  CatLayout,
  CatLayoutSection,
  CatSkeleton
} from 'components/blocks-components';
import PartsDiagram from 'components/parts-diagram';
import ProductDiagramTitle from '../product-diagram-title';
import ProductDiagramDropdown from '../product-diagram-dropdown/product-diagram-dropdown';
import {
  PCC_PDP_HeaderUpdateMobileView,
  PCC_PDP_PARTS_DIAGRAM_MOBILE
} from 'constants/featureFlag';
import { DealerData } from 'types/parts-diagram';
import { CartResponse } from 'services/cart/types';
import { usePriceAndAvailabilityQuery } from 'services/priceAvailability/use-query-price-availability';
import { getPrice } from 'utils/product-diagram';

interface ProductDiagramWrapperProps {
  isCatCorp: boolean;
  dealerData: DealerData;
  locale: string;
  storeFeatures: { [key: string]: boolean };
  dealerInformation: DealerResponse;
  isShowSourcingDetailEnabled: boolean;
  isDeliveryEnabled: boolean;
  partDetailsAndSchematicResponse: PartDetailsAndSchematicResponse[];
  serialNumber: string;
  model: string;
  partNumber: string;
  partName: string;
  fromProductPage?: boolean;
  contactDealerBtn?: React.ReactNode;
  cartDetails?: CartResponse;
  contactDealerAllProductsBtn?: React.ReactNode;
  isRTL?: boolean;
  currencyLdFeatureFlag?: boolean;
  storeId: string;
  langId: string;
  isHeadlessPriceAvailability_additional_sec?: boolean;
}

const ProductDiagramWrapper = ({
  isCatCorp,
  dealerData,
  locale,
  cartDetails,
  storeFeatures,
  dealerInformation,
  isShowSourcingDetailEnabled,
  isDeliveryEnabled,
  partDetailsAndSchematicResponse,
  serialNumber,
  model,
  partNumber,
  partName,
  fromProductPage,
  contactDealerBtn,
  contactDealerAllProductsBtn,
  storeId,
  langId,
  isRTL = false,
  currencyLdFeatureFlag = false,
  isHeadlessPriceAvailability_additional_sec = false
}: ProductDiagramWrapperProps) => {
  const {
    setIsCatCorp,
    serverData,
    setServerData,
    partsDetails,
    setAnalyticsCommonInfo,
    analyticsCommonInfo
  } = usePartDiagram();
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  //function is going to be implemented in US: 1232786
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handlePartView = () => {};

  const handleOnHover = (refNumber: string | null) => {
    setHoveredItem(refNumber);
  };

  useEffect(() => {
    setIsCatCorp(isCatCorp);
  }, [isCatCorp, setIsCatCorp]);

  useEffect(() => {
    setServerData({
      ...serverData,
      locale,
      dealerData,
      storefeatures: storeFeatures,
      dealerInformation,
      isShowSourcingDetailEnabled,
      isDeliveryEnabled
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    locale,
    dealerData,
    storeFeatures,
    dealerInformation,
    isShowSourcingDetailEnabled,
    isDeliveryEnabled
  ]);

  useEffect(() => {
    setAnalyticsCommonInfo({
      ...analyticsCommonInfo,
      dealerData,
      locale,
      model: serialNumber,
      currency: partsDetails?.currency
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, dealerData, partsDetails?.currency, serialNumber]);

  const [isLoading, setIsLoading] = useState(false);

  const [selectedTitle, setSelectedTitle] = useState(
    partDetailsAndSchematicResponse[0]?.title
  );

  const [selectedDiagramData, setSelectedDiagramData] = useState(
    partDetailsAndSchematicResponse[0]
  );
  const pricePartsList = selectedDiagramData.lineItems.map((item) => ({
    partNumber: `${item.partNumber.slice(0, -4)}-${item.partNumber.slice(-4)}`,
    quantity: item.quantity.trim().length === 0 ? '1' : item.quantity
  }));
  const isHeadlessPriceAvailability =
    isHeadlessPriceAvailability_additional_sec;

  const {
    data: priceData,
    isLoading: priceLoading,
    isSuccess,
    isError
  } = usePriceAndAvailabilityQuery({
    storeId,
    langId,
    partsList: pricePartsList,
    isHeadlessPriceAvailability
  });
  const combinedData = isSuccess
    ? (getPrice(
        [selectedDiagramData],
        priceData
      )[0] as unknown as PartDetailsAndSchematicResponse)
    : selectedDiagramData;
  const [selectedDiagram, setSelectedDiagram] = useState(
    partDetailsAndSchematicResponse[0]?.graphicSVG
  );

  const handleSelectionChange = (title: string) => {
    setSelectedTitle(title);
    setIsLoading(true);

    const updatedDiagramResponse = partDetailsAndSchematicResponse.find(
      (item) => item.title === title
    );

    if (updatedDiagramResponse) {
      setSelectedDiagramData(updatedDiagramResponse);
      setSelectedDiagram(updatedDiagramResponse?.graphicSVG);
    }
    setIsLoading(false);
  };
  const shouldShowDiagram = !isEmpty(selectedDiagram);

  const partsDiagramMobileFlag = storeFeatures?.[PCC_PDP_PARTS_DIAGRAM_MOBILE];
  const isPccPdpHeaderUpdateMobile =
    storeFeatures?.[PCC_PDP_HeaderUpdateMobileView];

  return (
    <>
      <div className="d-lg-flex justify-content-md-between justify-content-center align-items-baseline">
        <ProductDiagramTitle
          model={model}
          partNumber={partNumber}
          partName={partName}
        />

        <ProductDiagramDropdown
          selectOptions={partDetailsAndSchematicResponse?.map(
            (item) => item.title
          )}
          selectedTitle={selectedTitle}
          onSelect={handleSelectionChange}
        />
      </div>
      {priceLoading ? <CatSkeleton size="xxl" /> : null}
      {combinedData && (isSuccess || isError) && (
        <Conditional
          test={shouldShowDiagram}
          fallback={
            <PartsTableWrapper
              partDetailsAndSchematicsData={combinedData}
              handlePartView={handlePartView}
              fromProductPage={fromProductPage}
              isRTL={isRTL}
              currencyLdFeatureFlag={currencyLdFeatureFlag}
            />
          }
        >
          <CatLayout
            style={{
              ['--cat-sidebar-width' as string]: '405px'
            }}
            variant="right-sidebar"
            gap="sm"
            break="slower"
          >
            <CatLayoutSection>
              <PartsDiagram
                hoveredItem={hoveredItem}
                partDetailsAndSchematicsData={combinedData}
                svgContent={selectedDiagram}
                isLoading={isLoading}
                isMobile={partsDiagramMobileFlag}
                handleOnHover={handleOnHover}
                cartDetails={cartDetails}
                isZoomEnabled
              />
            </CatLayoutSection>
            <CatLayoutSection>
              <PartsOverview
                cartDetails={cartDetails}
                partDetailsAndSchematicsData={combinedData}
                handlePartView={handlePartView}
                onHover={handleOnHover}
                selectedItem={hoveredItem}
                partsDiagramMobileView={partsDiagramMobileFlag}
                isPccPdpHeaderUpdateMobile={isPccPdpHeaderUpdateMobile}
                fromProductPage={fromProductPage}
                contactDealerBtn={contactDealerBtn}
                contactDealerAllProductsBtn={contactDealerAllProductsBtn}
                isRTL={isRTL}
                currencyLdFeatureFlag={currencyLdFeatureFlag}
                isHeadlessPriceAvailability_additional_sec={
                  isHeadlessPriceAvailability_additional_sec
                }
              />
            </CatLayoutSection>
          </CatLayout>
        </Conditional>
      )}
    </>
  );
};

export default ProductDiagramWrapper;
